import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useSessionContext } from "@/contexts/SessionContext";
import { useTeleconsultoriaAvailability } from "../hooks/useTeleconsultoriaAvailability";
import { useTeleconsultoriaProfile } from "../profile/TeleconsultoriaProfileContext";
import { useTeleconsultoriaPresencePublisher } from "./useTeleconsultoriaPresencePublisher";

interface TeleconsultoriaPresenceState {
  /** Só a prestadora controla disponibilidade/presença. */
  canManageAvailability: boolean;
  isAvailable: boolean;
  isLoading: boolean;
  isSaving: boolean;
  setAvailable: (available: boolean) => Promise<void>;
}

const NOOP_STATE: TeleconsultoriaPresenceState = {
  canManageAvailability: false,
  isAvailable: false,
  isLoading: false,
  isSaving: false,
  setAvailable: async () => undefined,
};

const TeleconsultoriaPresenceContext =
  createContext<TeleconsultoriaPresenceState>(NOOP_STATE);

/**
 * Presença GLOBAL do teleconsultor.
 * - Carrega a disponibilidade uma vez (agenda de disponibilidade da prestadora).
 * - Mantém UM peer `tc-{cpf}` vivo em qualquer tela enquanto estiver disponível
 *   (antes só existia com a página de Teleconsultoria aberta).
 * - Troca de organização / logout: o peer é destruído (o efeito do publisher
 *   desliga quando deixa de ser prestadora ou quando o provider desmonta).
 * Montado uma única vez em AppProviders: ninguém mais pode publicar o peer,
 * senão o broker devolve `unavailable-id`.
 */
export function TeleconsultoriaPresenceProvider({ children }: { children: ReactNode }) {
  const { access } = useSessionContext();
  const { canManageAvailability } = useTeleconsultoriaProfile();
  const isEnabled = access?.type === "professional" && canManageAvailability;

  const { loading, saving, available, setAvailable } = useTeleconsultoriaAvailability({
    isEnabled,
  });
  useTeleconsultoriaPresencePublisher(isEnabled && available);

  const value = useMemo<TeleconsultoriaPresenceState>(
    () => ({
      canManageAvailability: isEnabled,
      isAvailable: available,
      isLoading: loading,
      isSaving: saving,
      setAvailable,
    }),
    [isEnabled, available, loading, saving, setAvailable],
  );

  return (
    <TeleconsultoriaPresenceContext.Provider value={value}>
      {children}
    </TeleconsultoriaPresenceContext.Provider>
  );
}

export function useTeleconsultoriaPresenceState(): TeleconsultoriaPresenceState {
  return useContext(TeleconsultoriaPresenceContext);
}
